import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Save } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import type {
  APIResponse,
  Campaign,
  CampaignChannel,
  CampaignPreviewResponse,
  CampaignStatus,
  CreateCampaignRequest,
  PromoCode,
  UpdateCampaignRequest,
} from '@/types'

interface CampaignDrawerProps {
  mode: 'create' | 'edit'
  initial?: Campaign | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface DraftState {
  name: string
  description: string
  status: CampaignStatus
  channelSet: CampaignChannel[]
  segmentKey: string
  promoCodeId: string
  subject: string
  body: string
  waTemplateName: string
  waTemplateParams: string[]
  mintPerCustomer: boolean
}

const DEFAULT_DRAFT: DraftState = {
  name: '',
  description: '',
  status: 'draft',
  channelSet: ['email', 'whatsapp_click'],
  segmentKey: 'birthdays_this_month',
  promoCodeId: '',
  subject: '',
  body: '',
  waTemplateName: '',
  waTemplateParams: [],
  mintPerCustomer: false,
}

const SEGMENT_OPTIONS: Array<{ key: string; label: string }> = [
  { key: 'birthdays_this_month', label: 'Birthdays this month' },
  { key: 'lapsed_60d', label: 'Lapsed (60+ days)' },
  { key: 'at_risk', label: 'At risk' },
  { key: 'vips', label: 'VIPs' },
  { key: 'first_timers_recent', label: 'First-timers (last 30d)' },
  { key: 'regulars', label: 'Regulars' },
]

const MERGE_TAGS = ['{{first_name}}', '{{name}}', '{{promo_code}}', '{{tenant_name}}', '{{review_url}}']

function campaignToDraft(c: Campaign): DraftState {
  const seg = (c.segment_def as { segment?: string })?.segment ?? ''
  return {
    name: c.name,
    description: c.description,
    status: c.status,
    channelSet: c.channel_set,
    segmentKey: seg,
    promoCodeId: c.promo_code_id ?? '',
    subject: c.message_subject,
    body: c.message_body,
    waTemplateName: c.wa_template_name ?? '',
    waTemplateParams: c.wa_template_params ?? [],
    mintPerCustomer: c.mint_per_customer ?? false,
  }
}

/**
 * Drawer for creating or editing a campaign. The merge-tag picker chips
 * insert into the focused subject/body field on click — the simplest
 * UX that beats "remember which tags are supported."
 */
export function CampaignDrawer({ mode, initial, open, onOpenChange }: CampaignDrawerProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [draft, setDraft] = useState<DraftState>(DEFAULT_DRAFT)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [focused, setFocused] = useState<'subject' | 'body' | null>(null)

  useEffect(() => {
    if (!open) return
    setDraft(initial ? campaignToDraft(initial) : DEFAULT_DRAFT)
  }, [open, initial])

  const promosQuery = useQuery<APIResponse<PromoCode[]>>({
    queryKey: ['promo-codes', { includeArchived: false, includeReceiptCodes: false }],
    queryFn: () => apiClient.listPromoCodes({ include_archived: false }),
    enabled: open,
    staleTime: 60_000,
  })
  const promos = promosQuery.data?.data ?? []

  // WhatsApp template registry — only campaign-purpose templates are
  // offered here (business-initiated sends must use an approved template).
  const waTemplatesQuery = useQuery({
    queryKey: ['marketing', 'wa-templates'],
    queryFn: () => apiClient.getWATemplates(),
    enabled: open && draft.channelSet.includes('whatsapp_api'),
    staleTime: Infinity,
  })
  const waTemplates = (waTemplatesQuery.data?.data?.templates ?? []).filter(
    (t) => t.purpose === 'campaign',
  )
  const selectedWATemplate = waTemplates.find((t) => t.name === draft.waTemplateName)

  const createMutation = useMutation({
    mutationFn: (data: CreateCampaignRequest) => apiClient.createCampaign(data),
    onSuccess: () => {
      toast({ title: 'Campaign created' })
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      onOpenChange(false)
    },
    onError: (err) => surfaceErr(err, toast, 'Create failed'),
  })
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCampaignRequest }) =>
      apiClient.updateCampaign(id, data),
    onSuccess: () => {
      toast({ title: 'Campaign updated' })
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      onOpenChange(false)
    },
    onError: (err) => surfaceErr(err, toast, 'Update failed'),
  })

  const previewMutation = useMutation({
    mutationFn: (id: string) => apiClient.previewCampaign(id),
  })
  const preview: CampaignPreviewResponse | undefined = previewMutation.data?.data

  const submitting = createMutation.isPending || updateMutation.isPending

  function toggleChannel(ch: CampaignChannel) {
    setDraft((d) => ({
      ...d,
      channelSet: d.channelSet.includes(ch)
        ? d.channelSet.filter((c) => c !== ch)
        : [...d.channelSet, ch],
    }))
  }

  function insertTag(tag: string) {
    if (focused === 'subject') {
      setDraft((d) => ({ ...d, subject: d.subject + tag }))
    } else if (focused === 'body') {
      setDraft((d) => ({ ...d, body: d.body + tag }))
    }
  }

  function handleSave() {
    if (draft.name.trim().length === 0) {
      toast({ title: 'Name required', variant: 'destructive' })
      return
    }
    const payload = {
      name: draft.name.trim(),
      description: draft.description.trim(),
      status: draft.status,
      channel_set: draft.channelSet,
      segment_def: { segment: draft.segmentKey },
      message_subject: draft.subject,
      message_body: draft.body,
      wa_template_name: draft.channelSet.includes('whatsapp_api') ? draft.waTemplateName : '',
      wa_template_params: draft.channelSet.includes('whatsapp_api') ? draft.waTemplateParams : [],
      mint_per_customer: draft.mintPerCustomer && Boolean(draft.promoCodeId),
    }
    if (mode === 'create') {
      const data: CreateCampaignRequest = {
        ...payload,
        ...(draft.promoCodeId ? { promo_code_id: draft.promoCodeId } : {}),
      }
      createMutation.mutate(data)
      return
    }
    if (!initial) return
    const data: UpdateCampaignRequest = { ...payload }
    if (draft.promoCodeId) {
      data.promo_code_id = draft.promoCodeId
    } else {
      data.clear_promo = true
    }
    updateMutation.mutate({ id: initial.id, data })
  }

  const promoOptions = useMemo(
    () => promos.filter((p) => p.is_active),
    [promos]
  )

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'New campaign' : `Edit ${initial?.name ?? ''}`}
          </DialogTitle>
          <DialogDescription>
            Choose a segment, attach a promo code, write the message. Use merge tags like{' '}
            <code className="text-sm">{'{{first_name}}'}</code> for personalisation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="camp-name">Name</Label>
              <Input
                id="camp-name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="March birthdays"
                maxLength={120}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select
                value={draft.status}
                onValueChange={(v) => setDraft({ ...draft, status: v as CampaignStatus })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="camp-desc">Description (internal)</Label>
            <Input
              id="camp-desc"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="What this campaign is for, only the team sees this"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Segment</Label>
              <Select
                value={draft.segmentKey}
                onValueChange={(v) => setDraft({ ...draft, segmentKey: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEGMENT_OPTIONS.map((s) => (
                    <SelectItem key={s.key} value={s.key}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Promo code (attribution)</Label>
              <Select
                value={draft.promoCodeId || 'none'}
                onValueChange={(v) => setDraft({ ...draft, promoCodeId: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No promo (no attribution)</SelectItem>
                  {promoOptions.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="font-mono">{p.code}</span> ·{' '}
                      {p.discount_type === 'percent' ? `${p.discount_value}%` : `PKR ${p.discount_value}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {draft.promoCodeId && (
                <label className="flex items-start gap-2 pt-1 text-sm font-normal">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={draft.mintPerCustomer}
                    onChange={(e) => setDraft({ ...draft, mintPerCustomer: e.target.checked })}
                  />
                  <span>
                    Mint a personal single-use code per recipient
                    <span className="block text-xs text-muted-foreground">
                      Each guest gets their own copy of this offer, bound to
                      them — staff can verify who's redeeming, and it can't be
                      shared.
                    </span>
                  </span>
                </label>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Channels</Label>
            <div className="flex flex-wrap gap-2">
              {(['email', 'whatsapp_click', 'whatsapp_api'] as CampaignChannel[]).map((ch) => (
                <Button
                  key={ch}
                  type="button"
                  size="sm"
                  variant={draft.channelSet.includes(ch) ? 'default' : 'outline'}
                  onClick={() => toggleChannel(ch)}
                  className="h-7 text-sm"
                >
                  {ch === 'email'
                    ? 'Email'
                    : ch === 'whatsapp_click'
                      ? 'WhatsApp (click)'
                      : 'WhatsApp (auto)'}
                </Button>
              ))}
            </div>
          </div>

          {draft.channelSet.includes('whatsapp_api') && (
            <div className="space-y-3 rounded-lg border border-border/80 p-3">
              <div className="space-y-1.5">
                <Label>WhatsApp template</Label>
                <Select
                  value={draft.waTemplateName || 'none'}
                  onValueChange={(v) => {
                    const tmpl = waTemplates.find((t) => t.name === v)
                    setDraft((d) => ({
                      ...d,
                      waTemplateName: v === 'none' ? '' : v,
                      // Prefill params with the obvious merge tags by name.
                      waTemplateParams: tmpl
                        ? tmpl.param_names.map((p) =>
                            ['first_name', 'name', 'promo_code', 'review_url'].includes(p)
                              ? `{{${p}}}`
                              : p === 'restaurant_name'
                                ? '{{tenant_name}}'
                                : '',
                          )
                        : [],
                    }))
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pick an approved template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— pick a template —</SelectItem>
                    {waTemplates.map((t) => (
                      <SelectItem key={t.name} value={t.name}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  WhatsApp only delivers pre-approved templates outside a live
                  chat — free text isn't allowed, so the body above is not used
                  for this channel.
                </p>
              </div>
              {selectedWATemplate && (
                <>
                  <p className="rounded bg-muted/50 p-2 text-sm">
                    {selectedWATemplate.preview}
                  </p>
                  {selectedWATemplate.param_names.map((pname, i) => (
                    <div key={pname} className="space-y-1">
                      <Label className="text-xs">
                        {'{{'}
                        {i + 1}
                        {'}}'} · {pname}
                      </Label>
                      <Input
                        value={draft.waTemplateParams[i] ?? ''}
                        onChange={(e) =>
                          setDraft((d) => {
                            const params = [...d.waTemplateParams]
                            params[i] = e.target.value
                            return { ...d, waTemplateParams: params }
                          })
                        }
                        placeholder={`{{${pname}}} or literal text`}
                        className="h-9 font-mono text-sm"
                      />
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="camp-subject">Subject (email)</Label>
            <Input
              id="camp-subject"
              value={draft.subject}
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
              onFocus={() => setFocused('subject')}
              placeholder="Happy birthday, {{first_name}}!"
              maxLength={200}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="camp-body">Body</Label>
            <Textarea
              id="camp-body"
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              onFocus={() => setFocused('body')}
              rows={5}
              placeholder="Hi {{first_name}}, happy birthday! Use {{promo_code}} on your next visit."
              maxLength={2000}
            />
            <div className="flex flex-wrap gap-1.5 pt-1">
              <span className="text-sm uppercase tracking-wider text-muted-foreground self-center mr-1">
                Insert:
              </span>
              {MERGE_TAGS.map((tag) => (
                <Badge
                  key={tag}
                  variant="outline"
                  className="cursor-pointer font-mono text-sm hover:bg-muted"
                  onClick={() => insertTag(tag)}
                >
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          {mode === 'edit' && initial && (
            <div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  if (initial.id) {
                    previewMutation.mutate(initial.id)
                    setPreviewOpen(true)
                  }
                }}
                disabled={previewMutation.isPending}
                className="gap-1.5"
              >
                {previewMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Preview with first recipient
              </Button>
              {previewOpen && preview && (
                <div className="mt-3 rounded-md border border-border/70 bg-muted/30 px-3 py-2 space-y-2 text-sm">
                  <div>
                    <span className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Subject</span>
                    <div className="font-medium">{preview.subject || '—'}</div>
                  </div>
                  <div>
                    <span className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Body</span>
                    <div className="whitespace-pre-wrap">{preview.body || '—'}</div>
                  </div>
                  {preview.warnings && preview.warnings.length > 0 && (
                    <div className="rounded bg-amber-50 px-2 py-1 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                      <strong>Heads up:</strong> {preview.warnings.join(', ')}
                    </div>
                  )}
                  {preview.sample_recipient?.name && (
                    <div className="text-sm text-muted-foreground">
                      Rendered for: {preview.sample_recipient.name}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={submitting} className="gap-1.5">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function surfaceErr(err: unknown, toast: ReturnType<typeof useToast>['toast'], title: string) {
  const msg = err instanceof Error ? err.message : String(err)
  toast({ title, description: msg, variant: 'destructive' })
}
